import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useAiStatus } from "@/lib/useAiStatus";
import PeriodoPicker, { prevMonthRange } from "@/components/controllo/PeriodoPicker";
import DipendenteCard from "@/components/controllo/DipendenteCard";
import AnalisiIA from "@/components/controllo/AnalisiIA";
import { Button } from "@/components/ui/button";
import { CalendarX2, ChevronDown, ChevronRight, Printer, RotateCcw, CheckCircle2 } from "lucide-react";

const fmtD = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

function Stat({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 min-w-[120px]" data-testid={testid}>
      <div className={`text-2xl font-heading font-black ${tone}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

export default function ControlloOrePage() {
  const { user } = useAuth();
  const ai = useAiStatus();
  const [[from, to], setRange] = useState(prevMonthRange());
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showZero, setShowZero] = useState(false);
  const [showIgn, setShowIgn] = useState(false);
  const canEdit = !!user?.can_edit_controllo_ore;

  const load = useCallback(async (f = from, t = to) => {
    setLoading(true);
    try {
      const { data } = await api.get("/controllo-ore", { params: { date_from: f, date_to: t } });
      setRes(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analisi non riuscita");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (user && user.can_view_controllo_ore === false) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="controllo-disabled">Il Controllo giornate mancanti non è abilitato per questo account.</div>;
  }
  const conOre = (res?.dipendenti || []).filter((e) => e.giorni_con_ore > 0);
  const zero = (res?.dipendenti || []).filter((e) => e.giorni_con_ore === 0);
  const zeroOpen = showZero || conOre.length === 0;
  const r = res?.riepilogo;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-5" data-testid="controllo-ore-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-black tracking-tight flex items-center gap-2"><CalendarX2 className="h-7 w-7 text-[#0044FF]" /> Controllo giornate mancanti</h1>
          <p className="text-sm text-slate-500 mt-1">Dipendenti senza ore a rapportino nei giorni lavorativi, non coperti da ferie/malattia/infortunio nel Programma. {canEdit ? "Seleziona i giorni e decidi: registra l'assenza (aggiorna il Programma), ignora o crea il rapportino." : "Sola visualizzazione."}</p>
        </div>
        <Button asChild variant="outline" size="sm"><Link to="/stampe" data-testid="controllo-to-stampe"><Printer className="h-4 w-4 mr-2" />Stampe ore</Link></Button>
      </div>

      <PeriodoPicker from={from} to={to} loading={loading} onChange={(f, t) => setRange([f, t])} onRun={() => load(from, to)} />

      {res && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="controllo-riepilogo">
            <Stat label="dipendenti controllati" value={r.controllati} testid="stat-controllati" />
            <Stat label="giorni lavorativi" value={res.giorni_lavorativi} testid="stat-giorni" />
            <Stat label="giornate senza ore" value={r.mancanti} tone={r.mancanti ? "text-red-600" : "text-emerald-600"} testid="stat-mancanti" />
            <Stat label="dipendenti con mancanze" value={r.dipendenti_con_mancanze} tone={r.dipendenti_con_mancanze ? "text-amber-600" : "text-emerald-600"} testid="stat-dip-mancanze" />
            <Stat label="coperte da assenze" value={r.assenze_coperte} tone="text-sky-700" testid="stat-assenze" />
            <Stat label="ignorate" value={r.ignorati} tone="text-slate-500" testid="stat-ignorati" />
            {r.weekend > 0 && <Stat label="weekend/festivi da verificare" value={r.weekend} tone="text-amber-700" testid="stat-weekend" />}
          </div>
          <div className="text-xs text-slate-500 flex flex-wrap gap-3" data-testid="controllo-legenda">
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-red-200 border border-red-300 mr-1" />previsto in cantiere con colleghi che hanno ore</span>
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-100 border border-amber-300 mr-1" />previsto in cantiere, nessun collega con ore</span>
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-sky-100 border border-sky-300 mr-1" />disponibile / da valutare</span>
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-white border border-slate-300 mr-1" />senza programma</span>
            <span>⚠ = sabato/domenica/festivo con cantiere attivo</span>
          </div>

          <AnalisiIA from={res.from} to={res.to} aiStatus={ai} disabled={loading || !res.dipendenti.length} />

          {!res.dipendenti.length && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 flex items-center gap-2" data-testid="controllo-tutto-ok">
              <CheckCircle2 className="h-5 w-5" /> Tutte le giornate del periodo {fmtD(res.from)} – {fmtD(res.to)} risultano coperte.
            </div>
          )}

          <div className="space-y-2" data-testid="controllo-lista">
            {conOre.map((e) => <DipendenteCard key={e.employee_id} emp={e} canEdit={canEdit} onDone={() => load(from, to)} />)}
          </div>

          {zero.length > 0 && (
            <div className="space-y-2">
              <button type="button" onClick={() => setShowZero((v) => !v)} className="flex items-center gap-1 text-sm font-semibold text-slate-700" data-testid="controllo-toggle-zero">
                {zeroOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {zero.length} dipendenti senza NESSUNA ora nel periodo (impiegati, assunti/cessati, lunghe assenze)
              </button>
              {zeroOpen && zero.map((e) => <DipendenteCard key={e.employee_id} emp={e} canEdit={canEdit} onDone={() => load(from, to)} />)}
            </div>
          )}

          {res.ignorati?.length > 0 && (
            <div className="space-y-1">
              <button type="button" onClick={() => setShowIgn((v) => !v)} className="flex items-center gap-1 text-sm font-semibold text-slate-500" data-testid="controllo-toggle-ignorati">
                {showIgn ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {res.ignorati.length} giornate ignorate
              </button>
              {showIgn && (
                <ul className="text-xs text-slate-600 space-y-1 pl-5">
                  {res.ignorati.map((i) => (
                    <li key={i.id} className="flex items-center gap-2">
                      <span>{fmtD(i.data)} — {i.nome || i.employee_id}{i.motivo ? ` (${i.motivo})` : ""}</span>
                      {canEdit && (
                        <button type="button" className="text-[#0044FF] inline-flex items-center gap-0.5" data-testid={`controllo-ripristina-${i.employee_id}-${i.data}`}
                          onClick={async () => { await api.delete("/controllo-ore/ignora", { params: { employee_id: i.employee_id, data: i.data } }); load(from, to); }}>
                          <RotateCcw className="h-3 w-3" />ripristina
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
